import { SearchIcon } from "lucide-react";
import styles from "./ActivityTab.module.scss";
import classNames from "classnames/bind";
import { Dropdown } from "~/components/common/Dropdown/Dropdown";
import { useEffect, useState, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { handleGetActivities } from "~/thunks/crm/crmThunk";
import { IActivityLog } from "~/utils/interface/crm";

const cx = classNames.bind(styles);

const ActivityTab = () => {
  const dispatch = useAppDispatch();
  const { activities, activitiesLoading, activitiesCurrentPage, activitiesTotalPage } = useAppSelector((state) => state.crm);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  
  const customerId = window.location.pathname.split("/").pop() || "";

  useEffect(() => {
    if (customerId) {
      dispatch(handleGetActivities({
        customerId,
        params: {
          page: currentPage,
          limit: 10,
          sortBy: "createdAt",
          sortType: "DESC"
        }
      }));
    }
  }, [dispatch, customerId, currentPage]);

  const filteredActivities = useMemo(() => {
    return activities.filter((activity) => {
      // Filter by activity type
      if (filterType !== "all" && activity.activityType !== filterType) {
        return false;
      }

      // Filter by search term
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          (activity.content?.toLowerCase().includes(searchLower)) ||
          (activity.subject?.toLowerCase().includes(searchLower)) ||
          (activity.emailFrom?.toLowerCase().includes(searchLower)) ||
          (activity.emailTo?.toLowerCase().includes(searchLower));
        
        if (!matchesSearch) {
          return false;
        }
      }

      return true;
    });
  }, [activities, searchTerm, filterType]);

  const getActivityTypeDisplay = (type: string) => {
    switch (type) {
      case 'note': return 'Note';
      case 'email': return 'Email';
      case 'sms': return 'Text';
      case 'call': return 'Call';
      case 'meeting': return 'Meeting';
      default: return type;
    }
  };

  const getActivityTypeClass = (type: string) => {
    switch (type) {
      case 'note': return 'type-note';
      case 'email': return 'type-email';
      case 'sms': return 'type-text';
      case 'call': return 'type-call';
      case 'meeting': return 'type-meeting';
      default: return 'type-note';
    }
  };

  const renderActivityContent = (activity: IActivityLog) => {
    return (
      <div className={cx("table-cell", "content-cell")}>
        {activity.activityType === 'note' && (
          <div className={cx("row-content")}>
            <h3 className={cx("title")}>Added by:</h3>
            <p className={cx("label")}>{activity.createdBy?.name}</p>
          </div>
        )}

        {activity.activityType === 'email' && (
          <>
            {activity.subject && (
              <div className={cx("row-content")}>
                <h3 className={cx("title")}>Subject:</h3>
                <p className={cx("label")}>{activity.subject}</p>
              </div>
            )}
            {activity.emailFrom && (
              <div className={cx("row-content")}>
                <h3 className={cx("title")}>From:</h3>
                <p className={cx("label")}>{activity.emailFrom}</p>
              </div>
            )}
            {activity.emailTo && (
              <div className={cx("row-content")}>
                <h3 className={cx("title")}>To:</h3>
                <p className={cx("label")}>{activity.emailTo}</p>
              </div>
            )}
          </>
        )}

        {activity.activityType === 'sms' && (
          <div className={cx("row-content")}>
            <h3 className={cx("title")}>Direction:</h3>
            <p className={cx("label")}>{activity.direction}</p>
          </div>
        )}

        <p className={cx("content")}>{activity.content}</p>
      </div>
    );
  };

  if (activitiesLoading) {
    return <div className={cx("loading")}>Loading activities...</div>;
  }

  return (
    <div className={cx("activity-tab-container")}>
      <div className={cx("activity-header")}>
        <h2 className={cx("activity-title")}>Activity</h2>

        <div className={cx("actions-header")}>
          <div className={cx("search")}>
            <SearchIcon className={cx("search-icon")} />
            <input
              type="text"
              placeholder="Search activities..."
              className={cx("search-input")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="w-[124px]">
            <Dropdown
              value={filterType}
              onChange={(value) => setFilterType(value)}
              options={[
                { value: "all", label: "ALL" },
                { value: "sms", label: "Text" },
                { value: "email", label: "Email" },
                { value: "note", label: "Note" }
              ]}
              placeholder="Select type"
            />
          </div>
        </div>
      </div>

      <table className={cx("activity-table")}>
        <thead>
          <tr>
            <th className={cx("table-header", "w-[25%]")}>Posted On</th>
            <th className={cx("table-header", "w-[75%]")}>Content</th>
          </tr>
        </thead>
        <tbody>
          {filteredActivities.map((activity) => (
            <tr key={activity.id}>
              <td>
                <div className={cx("table-cell", "posted-on-cell")}>
                  <div
                    className={cx(
                      "type",
                      getActivityTypeClass(activity.activityType)
                    )}
                  >
                    {getActivityTypeDisplay(activity.activityType)?.toUpperCase()}
                  </div>
                  <h3 className={cx("date")}>
                    {new Date(activity.createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </h3>
                  <p className={cx("time")}>
                    {new Date(activity.createdAt).toLocaleString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })}
                  </p>
                </div>
              </td>
              <td>
                {renderActivityContent(activity)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {filteredActivities.length === 0 && (
        <div className={cx("no-activities")}>
          <p>No activities found.</p>
        </div>
      )}
    </div>
  );
};

export default ActivityTab;
